import type { DatePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import { clientConfig } from 'pos-client/clientConfig.ts';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);

export const dateFormatString =
  clientConfig.locale === 'en-US' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';

export const dateFormatter = new Intl.DateTimeFormat(clientConfig.locale, {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
export const timeFormatter = new Intl.DateTimeFormat(clientConfig.locale, {
  hour: 'numeric',
  minute: '2-digit',
});

export const dateWithTimeFormatter12Hr = new Intl.DateTimeFormat(
  clientConfig.locale,
  {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  }
);
export const dateWithTimeformatter24Hr = new Intl.DateTimeFormat(
  clientConfig.locale,
  {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  }
);
export const dateFormatterTimezone = (timeZone: string | null | undefined) => {
  return new Intl.DateTimeFormat(clientConfig.locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timeZone && { timeZone }),
  });
};

// Format date with time taking into account timezone
export const createDateWithTimeFormatterTimezone = (
  timeZone: string | undefined | null
) => {
  return new Intl.DateTimeFormat(clientConfig.locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone && { timeZone }),
  });
};

export const utcDateFormatter = new Intl.DateTimeFormat(clientConfig.locale, {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: 'UTC',
});
export const utcTimeFormatter = new Intl.DateTimeFormat(clientConfig.locale, {
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'UTC',
});
export const utcDateTimeFormatter = new Intl.DateTimeFormat(
  clientConfig.locale,
  {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }
);

export const formatDateWithFallback = (
  value: Date | string | null | undefined,
  formatter: Intl.DateTimeFormat,
  fallback = '-'
) => {
  if (!value) {
    return fallback;
  }

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return formatter.format(date);
};

/**
 * Formats date strings into an ISOString for endpoints like getLocationLinkedTechnicianUsers
 *
 * Default Format <DD/MM/YYYY HH:mm:ss>
 *
 * Handles nullish values to make FE code cleaner instead of adding ternarys or logical operators
 * @param dateStamp [string | undefined | null]
 * @param format [string]
 * @returns string
 */

export const convertDateStringToISOString = (
  dateStamp: string | undefined | null,
  format = 'DD/MM/YYYY HH:mm:ss'
) => {
  const date = dayjs(dateStamp, format, true);

  if (date.isValid()) {
    return date.toISOString();
  }

  return '';
};

export const convertISOStringToLocalDate = (isoString?: string) =>
  isoString && dayjs(isoString).format(dateFormatString);

/**
 * Returns true if the given value can be parsed into a valid JS Date.
 * Handles null, undefined, empty strings and NaN dates.
 *
 * @param value [string | number | Date | null | undefined] The value to validate.
 * @returns [boolean] True if the value produces a valid Date, false otherwise.
 */
export const isValidDateInput = (
  value?: string | number | Date | null
): boolean => {
  if (value === null || value === undefined || value === '') return false;
  const date = value instanceof Date ? value : new Date(value);
  return !Number.isNaN(date.getTime());
};

/**
 * Clears second for time formatted as <HH:mm:ss>
 * @param time [string]
 * @returns string
 */
export const clearSeconds = (time: string): string => {
  if (!time) return time;
  if (typeof time !== 'string') return time;
  const split = time.split(':');
  // If it's not 3 then it doesn't follow the HH:mm:ss format
  if (split.length !== 3) return time;

  split.pop();
  return split.join(':');
};

// Time is in format of HH:mm:ss
export const durationToMinutes = (time: string) => {
  const dateNow = dayjs('00:00:00', 'HH:mm:ss');
  const date = dayjs(time, 'HH:mm:ss');

  if (date.isValid()) {
    return date.diff(dateNow, 'minutes');
  }

  return null;
};

/**
 * returns date in format "Weekday day+ordinal month year , time" (Tue 2nd Apr 2024, 11:11 AM)
 * @param date [date]
 * @returns string
 */
export const dateWithDayMonthYearTimeFormatter = (date: Date) => {
  return dayjs(date)
    .locale(clientConfig.locale)
    .format('ddd Do MMM YYYY, h:mm A');
};

export const datetimeAtTimezone = (targetTimezone?: string) => {
  // Current date and time

  const now = dayjs();

  if (!targetTimezone) return now;

  // Get the current time in the target timezone
  const timeInTargetTimezone = now.tz(targetTimezone);

  // Extract time components from the target timezone
  const day = timeInTargetTimezone.day();
  const hours = timeInTargetTimezone.hour();
  const minutes = timeInTargetTimezone.minute();
  const seconds = timeInTargetTimezone.second();

  // Apply these time components to the current local date
  // Returning the new Day.js object which has the adjusted time
  return now.day(day).hour(hours).minute(minutes).second(seconds);
};

export const setTz = (date: string, tz?: string | null) => {
  if (tz) {
    return dayjs(date).tz(tz).format();
  }

  return date;
};

export const dayJsWeekdayAsString = (date: Dayjs) => {
  const weekdayFromDayJsDate = date.weekday();

  const weekdayMap = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  return weekdayMap[weekdayFromDayJsDate];
};

export const getYears = () => {
  // Vehicles start from 1962 in the Epicor system ¯\_(ツ)_/¯
  const start = 1962;
  // This year + 1 is the max year of a vehicle to account for parts from an unreleased vehicle
  const end = new Date().getFullYear() + 1;
  return Array(end - start + 1)
    .fill(0)
    .map((_, idx) => ({
      value: start + idx,
      label: `${start + idx}`,
    }))
    .reverse();
};

export const customWeekStartEndFormat: DatePickerProps['format'] = (value) => {
  const week = dayjs(value);
  const format = (d: dayjs.Dayjs) => d.format('Do MMM YYYY');

  return `${format(week.startOf('week'))} - ${format(week.endOf('week'))}`;
};
